/**
 * Instance settings drawer — setup, monitor, definition, and history from the run workspace.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { BindingGovernanceSummary, BindingStatusBadge } from '@/components/ai-studio/bindings';
import {
  InstanceIdentityCard,
  useInstanceEditor,
  type InstanceJsonField,
} from '@/components/ai-studio/instances';
import { InstanceEffectiveAgentPanel } from '@/components/ai-studio/instances/InstanceEffectiveAgentPanel';
import { InstanceMonitorPanel } from '@/components/ai-studio/instances/InstanceMonitorPanel';
import { InstanceOutputsHistoryPanel } from '@/components/ai-studio/instances/InstanceOutputsHistoryPanel';
import { InstanceSetupHistoryPanel } from '@/components/ai-studio/instances/InstanceSetupHistoryPanel';
import { JsonPanel } from '@/components/ai-studio/versions';
import { STUDIO_BINDING_STATUSES } from '@/types/aiStudioAgent';
import type { StudioBindingStatus } from '@/types/aiStudioAgent';

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';

type InstanceSettingsTab = 'overview' | 'monitor' | 'setup-history' | 'outputs-history';

interface InstanceSettingsDrawerProps {
  instanceId: string;
  open: boolean;
  onClose: () => void;
}

export const InstanceSettingsDrawer: React.FC<InstanceSettingsDrawerProps> = ({
  instanceId,
  open,
  onClose,
}) => {
  const { t } = useLanguage();
  const [tab, setTab] = useState<InstanceSettingsTab>('overview');
  const editor = useInstanceEditor(open ? instanceId : undefined);
  const { draft, instance } = editor;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-3xl flex-col border-l border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-label={t('aiStudio.workspace.instanceSettings')}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-[var(--color-surface-text)]">
              {instance
                ? t('aiStudio.instances.editorTitle', { name: instance.instanceName })
                : t('aiStudio.workspace.instanceSettings')}
            </h2>
            {instance && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <BindingStatusBadge status={instance.status} />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
            aria-label={t('aiStudio.workspace.closeSettings')}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="flex-shrink-0 border-b border-[var(--color-border)] px-4 py-2">
          <nav className="flex flex-wrap gap-2">
            {(
              [
                ['overview', 'aiStudio.instances.tabOverview'],
                ['monitor', 'aiStudio.instances.tabMonitor'],
                ['setup-history', 'aiStudio.instances.tabSetupHistory'],
                ['outputs-history', 'aiStudio.instances.tabOutputsHistory'],
              ] as const
            ).map(([key, labelKey]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`rounded border px-3 py-1.5 text-base ${
                  tab === key
                    ? 'border-[var(--color-info)] text-[var(--color-surface-text)]'
                    : 'border-[var(--color-border)] text-[var(--color-surface-text-secondary)]'
                }`}
              >
                {t(labelKey)}
              </button>
            ))}
          </nav>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {editor.loading && (
            <p className="text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instances.loading')}
            </p>
          )}

          {!editor.loading && (!instance || !draft) && (
            <p role="alert" className="text-base text-[var(--color-error)]">
              {t(editor.errorKey ?? 'aiStudio.instances.notFound')}
            </p>
          )}

          {!editor.loading && instance && draft && (
            <>
              {tab === 'monitor' && <InstanceMonitorPanel instanceId={instance.id} />}
              {tab === 'setup-history' && <InstanceSetupHistoryPanel instanceId={instance.id} />}
              {tab === 'outputs-history' && (
                <InstanceOutputsHistoryPanel instanceId={instance.id} />
              )}

              {tab === 'overview' && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/ai-studio/instances/${instance.id}/provision`}
                      className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-base text-[var(--color-info)] hover:bg-[var(--color-surface-hover)]"
                    >
                      {t('aiStudio.provision.continueSetup')}
                    </Link>
                    <button
                      type="button"
                      onClick={() => void editor.save()}
                      disabled={editor.saving || !editor.dirty || !editor.jsonValid}
                      className="rounded-md bg-[var(--color-accent-bg)] px-3 py-1.5 text-base text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
                    >
                      {editor.saving ? t('aiStudio.common.saving') : t('aiStudio.common.save')}
                    </button>
                  </div>

                  <InstanceIdentityCard instance={instance} />
                  <InstanceEffectiveAgentPanel instanceId={instance.id} />

                  {editor.setupStatus?.configured !== true && (
                    <p className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-4 py-2 text-base text-[var(--color-surface-text-secondary)]">
                      {t('aiStudio.instances.setupRequired')}
                    </p>
                  )}

                  {editor.errorKey && (
                    <div
                      role="alert"
                      className="rounded border border-[var(--color-error)] px-4 py-2 text-base text-[var(--color-error)]"
                    >
                      {t(editor.errorKey)}
                    </div>
                  )}

                  <BindingGovernanceSummary
                    executionMode={instance.executionMode}
                    versionAutonomyFences={instance.versionAutonomyFences}
                  />

                  <section className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className={LABEL_CLASS} htmlFor="drawer-instance-name">
                        {t('aiStudio.instances.fieldName')}
                      </label>
                      <input
                        id="drawer-instance-name"
                        className={FIELD_CLASS}
                        value={draft.instanceName}
                        disabled={instance.status === 'archived'}
                        onChange={(event) => editor.setField('instanceName', event.target.value)}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="drawer-instance-status">
                        {t('aiStudio.instances.fieldStatus')}
                      </label>
                      <select
                        id="drawer-instance-status"
                        className={FIELD_CLASS}
                        value={draft.status}
                        onChange={(event) =>
                          editor.setField('status', event.target.value as StudioBindingStatus)
                        }
                      >
                        {STUDIO_BINDING_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {t(`aiStudio.bindingStatus.${status}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="drawer-instance-pack">
                        {t('aiStudio.instances.fieldPack')}
                      </label>
                      <select
                        id="drawer-instance-pack"
                        className={FIELD_CLASS}
                        value={draft.industryPackId}
                        disabled={instance.status === 'archived'}
                        onChange={(event) => editor.setField('industryPackId', event.target.value)}
                      >
                        <option value="">{t('aiStudio.common.none')}</option>
                        {editor.packs.map((pack) => (
                          <option key={pack.id} value={pack.id}>
                            {pack.name} ({pack.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  </section>

                  <section className="grid gap-4 lg:grid-cols-2">
                    <JsonPanel
                      label={t('aiStudio.instances.instanceSetup')}
                      hint={t('aiStudio.instances.instanceSetupHint')}
                      value={draft.instanceSetup}
                      documentKey={`${instance.id}:${instance.updatedAt}:instanceSetup`}
                      disabled={instance.status === 'archived'}
                      onChange={(value) =>
                        editor.setField('instanceSetup', value as Record<string, unknown>)
                      }
                      onValidityChange={(valid) =>
                        editor.setJsonValidity('instanceSetup' as InstanceJsonField, valid)
                      }
                    />
                    <JsonPanel
                      label={t('aiStudio.bindings.fenceOverrides')}
                      hint={t('aiStudio.bindings.fenceOverridesHint')}
                      value={draft.autonomyFencesOverride}
                      documentKey={`${instance.id}:${instance.updatedAt}:fences`}
                      disabled={instance.status === 'archived'}
                      onChange={(value) =>
                        editor.setField('autonomyFencesOverride', value as Record<string, unknown>)
                      }
                      onValidityChange={(valid) =>
                        editor.setJsonValidity('autonomyFencesOverride' as InstanceJsonField, valid)
                      }
                    />
                  </section>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};
